import type { UserPreferences } from '~/features/user/models/user-preferences';

import { languageList } from '../languages';

type ResolveLanguageOptions = {
  browserLocale?: string | null;
  preferredLanguage?: string | null;
  preferredLanguageIsManual?: boolean | null;
};

type ResolveLanguageForSSROptions = {
  acceptLanguageHeader?: string | null;
  nextLocaleCookie?: string | null;
  userPreferences?: Pick<UserPreferences, 'preferredLanguage' | 'preferredLanguageIsManual'>;
};

const DEFAULT_LANGUAGE = 'en-US';

let supportedPreferenceCodes: Map<string, string> | null = null;

function getSupportedPreferenceCodes(): Map<string, string> {
  if (supportedPreferenceCodes) {
    return supportedPreferenceCodes;
  }

  // Derive the BCP-47 -> preference-code lookup from the canonical locale list so
  // it can never drift when a locale is added or removed. Each supported language
  // contributes both its id (e.g. "de-DE") and its preferenceCode (e.g. "de").
  const preferenceCodes = new Map<string, string>();
  for (const language of languageList ?? []) {
    if (!('preferenceCode' in language)) continue;
    preferenceCodes.set(language.id.toLowerCase(), language.preferenceCode);
    preferenceCodes.set(language.preferenceCode.toLowerCase(), language.preferenceCode);
  }
  // Aliases the canonical list does not encode: bare macrolanguage tags and the
  // region variant browsers commonly send that map to a supported preference code.
  preferenceCodes.set('en', DEFAULT_LANGUAGE);
  preferenceCodes.set('id', 'id-ID');
  preferenceCodes.set('ja-jp', 'ja');
  preferenceCodes.set('pt', 'pt-PT');

  supportedPreferenceCodes = preferenceCodes;
  return supportedPreferenceCodes;
}

export function normalizeToSupportedPreferenceCode(languageCode?: string | null): string {
  if (!languageCode) {
    return DEFAULT_LANGUAGE;
  }

  return getSupportedPreferenceCodes().get(languageCode.toLowerCase()) ?? DEFAULT_LANGUAGE;
}

// Returns the supported language code for the primary Accept-Language tag, or
// English when that primary tag is unsupported. Secondary q-value entries are
// intentionally ignored to match the client, which resolves from navigator.language.
// Returns null when the header is absent so callers can fall back to other sources.
//
// We deliberately do not use the `accept-language` npm package (used by the
// cf-worker for q-value best-match) here: SSR must mirror the client's
// primary-tag-only behavior so the server and client resolve to the same language.
export function parseAcceptLanguageToSupportedCode(acceptLanguageHeader?: string | null): string | null {
  if (!acceptLanguageHeader) {
    return null;
  }

  const primaryLanguageTag = acceptLanguageHeader.split(',')[0]?.split(';')[0]?.trim();
  return normalizeToSupportedPreferenceCode(primaryLanguageTag);
}

export function resolveLanguage({
  browserLocale,
  preferredLanguage,
  preferredLanguageIsManual,
}: ResolveLanguageOptions): string {
  if (preferredLanguageIsManual && preferredLanguage) {
    return normalizeToSupportedPreferenceCode(preferredLanguage);
  }

  return normalizeToSupportedPreferenceCode(browserLocale);
}

export function resolveLanguageForSSR({
  acceptLanguageHeader,
  nextLocaleCookie,
  userPreferences,
}: ResolveLanguageForSSROptions): string {
  // The live Accept-Language header always reflects the current browser
  // language; the NEXT_LOCALE cookie is a stale proxy that only updates after
  // hydration, so prefer the header and keep the cookie as a fallback.
  return resolveLanguage({
    browserLocale: parseAcceptLanguageToSupportedCode(acceptLanguageHeader) ?? nextLocaleCookie,
    preferredLanguage: userPreferences?.preferredLanguage,
    preferredLanguageIsManual: userPreferences?.preferredLanguageIsManual,
  });
}
